import React from 'react';
import { ArrowUp, ArrowDown, ChevronsUpDown, Rows3, Rows2, Square } from 'lucide-react';

// Density → row vertical padding. Shared across every list view so the
// Compact/Cosy/Comfortable toggle behaves identically everywhere.
export const densityRowClass = (density) => {
  if (density === 'compact') return 'px-4 py-1.5';
  if (density === 'comfortable') return 'px-4 py-5';
  return 'px-4 py-3'; // cosy (default)
};

// Persisted density state, keyed per list so each view remembers its own choice.
// Falls back to the global Quick-Settings density (guardian_density) when this
// list has no saved choice yet, so the top-bar control is felt everywhere.
export const useDensity = (storageKey, initial = 'cosy') => {
  const [density, setDensity] = React.useState(() => {
    try {
      return localStorage.getItem(storageKey)
        || localStorage.getItem('guardian_density')
        || initial;
    } catch { return initial; }
  });
  React.useEffect(() => {
    try { localStorage.setItem(storageKey, density); } catch { /* private mode */ }
  }, [storageKey, density]);
  return [density, setDensity];
};

export const DensityToggle = ({ density, onChange }) => (
  <div className="flex items-center gap-2 shrink-0" data-testid="density-toggle">
    <span className="text-sm text-muted-foreground">Density:</span>
    <div className="flex items-center gap-1 bg-secondary rounded-md p-0.5">
      {[
        { value: 'compact', icon: Rows3, label: 'Compact' },
        { value: 'cosy', icon: Rows2, label: 'Cosy' },
        { value: 'comfortable', icon: Square, label: 'Comfortable' },
      ].map(opt => {
        const Icon = opt.icon;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            title={opt.label}
            aria-label={`${opt.label} density`}
            data-testid={`density-${opt.value}-btn`}
            className={`p-1.5 rounded transition-colors ${
              density === opt.value ? 'bg-[#FFD108] text-black' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
          </button>
        );
      })}
    </div>
  </div>
);

// Sortable column header. Click cycles asc → desc on the active column,
// and starts fresh (asc) when a new column is picked.
export const SortableTh = ({ sortKey, label, current, dir, onSort, align = 'left', className = '' }) => {
  const active = current === sortKey;
  const Arrow = active ? (dir === 'desc' ? ArrowDown : ArrowUp) : ChevronsUpDown;
  return (
    <th className={`text-${align} p-4 ${className}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1.5 font-semibold uppercase tracking-wider text-[11px] transition-colors ${
          active ? 'text-[#C9A400]' : 'text-muted-foreground hover:text-foreground'
        }`}
        data-testid={`sort-${sortKey}`}
        aria-label={`Sort by ${label} ${active ? (dir === 'desc' ? 'descending' : 'ascending') : ''}`}
      >
        {label}
        <Arrow className={`w-3 h-3 ${active ? '' : 'opacity-50'}`} />
      </button>
    </th>
  );
};

// Local sort state + toggle helper.
export const useSortState = (initialKey = '', initialDir = 'asc') => {
  const [sortKey, setSortKey] = React.useState(initialKey);
  const [sortDir, setSortDir] = React.useState(initialDir);
  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  return { sortKey, sortDir, toggleSort, setSortKey, setSortDir };
};

// Client-side sort for list views that load a full array (Work Orders, Maintenance).
// `accessors` maps a sortKey → value extractor for computed/nested fields.
export const clientSort = (rows, sortKey, sortDir, accessors = {}) => {
  if (!sortKey) return rows;
  const get = accessors[sortKey] || ((r) => r[sortKey]);
  const sorted = [...rows].sort((a, b) => {
    const av = get(a);
    const bv = get(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return av - bv;
    return String(av).localeCompare(String(bv), undefined, { numeric: true });
  });
  return sortDir === 'desc' ? sorted.reverse() : sorted;
};
